import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, Check, ArrowRight, Mail, Inbox, MailCheck, Paperclip } from "lucide-react";
import { SITE } from "@/lib/site";
import { parseEmail, EMAIL_EXAMPLES } from "@/lib/demo-engine";
import { BrowserFrame, Pill, Btn, Panel } from "@/components/mockups/primitives";
import { cn } from "@/lib/utils";

const MARK = { lime: "bg-mk-lime/30", blue: "bg-mk-blue/40", amber: "bg-amber-400/30", red: "bg-red-400/30" };
const STAGE_FOR = { contact: 0, client: 1, property: 1, trade: 2, priority: 2, asset: 3, access: 3, attachments: 3, history: 3, phone: 0 };
const STAGES = ["Reading sender & signature", "Matching client & property", "Classifying trade & urgency", "Extracting access, assets & attachments"];
const fade = { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 } };

const Highlighted = ({ text, spans }) => {
  const parts = [];
  let i = 0;
  spans.forEach((s, k) => { if (s.start > i) parts.push(text.slice(i, s.start)); parts.push(<mark key={k} className={cn("rounded px-0.5 text-mk-text", MARK[s.tone])}>{text.slice(s.start, s.end)}</mark>); i = s.end; });
  if (i < text.length) parts.push(text.slice(i));
  return <>{parts}</>;
};

const JobCard = ({ p, stage, converted, onConvert }) => {
  const done = stage >= STAGES.length;
  const isDoc = p.kind === "supplier_doc";
  return (
    <div className="rounded-lg border border-mk-border p-2.5 text-[11px] h-full flex flex-col" data-testid="email-demo-card">
      <div className="flex items-center justify-between gap-2 text-[10px]">
        <span className="flex items-center gap-1.5 text-mk-lime"><Sparkles className="h-3 w-3" />{isDoc ? "Supplier document · files to job" : "Job card"}{!done && <Loader2 className="h-3 w-3 animate-spin" />}</span>
        {done && !converted && <Pill tone={p.urgency === "Emergency" ? "red" : p.urgency === "High" ? "amber" : "dim"}>{p.urgency}</Pill>}
        {converted && <Pill tone="green" data-testid="email-demo-converted">{isDoc ? `Filed to ${p.jobRef || "job"}` : "JOB-C2A1 created"}</Pill>}
      </div>
      <div className="mt-1.5 font-semibold text-mk-text leading-snug" data-testid="email-demo-title">{p.title}</div>
      {!done && <div className="mt-1 text-[10px] text-mk-muted" data-testid="email-demo-stage">{STAGES[stage]}…</div>}
      <div className="mt-2 grid grid-cols-2 gap-1.5 text-[10px]">
        {p.fields.map((f) => {
          const ready = stage > STAGE_FOR[f.key];
          return (
            <div key={f.key} className={cn("rounded-md border px-2 py-1 min-w-0 transition-colors duration-500", ready ? (f.ok ? "border-mk-border bg-mk-bg" : "border-dashed border-mk-strong bg-mk-bg/40") : "border-mk-border/40 bg-mk-bg/20")} data-testid={`email-demo-field-${f.key}`}>
              <div className="text-mk-muted flex items-center gap-1">{f.label}{ready && f.ok && <Check className="h-2.5 w-2.5 text-mk-lime" />}</div>
              {ready ? <motion.div {...fade} className={cn("truncate", f.tone === "red" ? "text-red-300" : f.tone === "amber" ? "text-amber-300" : f.ok ? "text-mk-text" : "text-mk-muted italic")} title={f.value}>{f.value}</motion.div> : <div className="h-3.5 w-2/3 rounded bg-mk-raised/60 animate-pulse mt-0.5" />}
            </div>
          );
        })}
      </div>
      {done && !converted && (
        <motion.div {...fade} className="mt-2.5 flex gap-1.5">
          <Btn className="flex-1 justify-center cursor-pointer" onClick={onConvert} role="button" data-testid="email-demo-convert">{isDoc ? <><Paperclip className="h-3 w-3" />File to {p.jobRef || "job"}</> : "Convert to job"}</Btn>
          <Btn tone="ghost" className="justify-center">Reply</Btn>
        </motion.div>
      )}
      {converted && (
        <motion.div {...fade} className="mt-2.5 rounded-md border border-mk-lime/40 bg-mk-lime/10 p-2 text-[10px] text-mk-dim">
          <div className="flex items-center gap-1.5 text-mk-lime font-semibold"><MailCheck className="h-3 w-3" />{isDoc ? "Attached to the job pack · supplier marked complete" : "Job created · thread attached · requester notified"}</div>
          <div className="mt-1">{isDoc ? "Certificate and invoice filed; Compliance Hub updated; invoice queued for approval." : `Assigned via the picker or AI Supplier Matching · ${p.urgency === "Emergency" ? "routed to the emergency pipeline" : "SLA timer started"} · every reply lands on JOB-C2A1.`}</div>
        </motion.div>
      )}
      <div className="mt-auto pt-2 text-[9px] text-mk-muted">Thread stays on the job · requester gets status updates on the same email</div>
    </div>
  );
};

export default function EmailDemo() {
  const [text, setText] = useState(EMAIL_EXAMPLES[0].text);
  const [parsed, setParsed] = useState(null);
  const [stage, setStage] = useState(-1);
  const [converted, setConverted] = useState(false);
  useEffect(() => {
    if (stage < 0 || stage >= STAGES.length) return undefined;
    const t = setTimeout(() => setStage((s) => s + 1), 550);
    return () => clearTimeout(t);
  }, [stage]);
  const run = () => { if (!text.trim()) return; setParsed(parseEmail(text)); setConverted(false); setStage(0); };
  const reset = (t) => { setText(t); setParsed(null); setStage(-1); setConverted(false); };
  const busy = stage >= 0 && stage < STAGES.length;
  const found = useMemo(() => parsed?.fields.filter((f) => f.ok).length ?? 0, [parsed]);

  return (
    <div className="grid lg:grid-cols-12 gap-8 items-start" data-testid="email-demo">
      <div className="lg:col-span-5 min-w-0">
        <label className="text-xs font-semibold uppercase tracking-wider text-mk-text" htmlFor="email-demo-input">Paste an email</label>
        <textarea id="email-demo-input" value={text} onChange={(e) => setText(e.target.value)} rows={11} maxLength={1500} placeholder={"From: someone@client.com\nSubject: Leak under the sink, Flat 2\n\nHi, there's a leak under the kitchen sink at 12 High Street…"} className="input-dark mt-2 resize-y leading-relaxed font-mono text-xs" data-testid="email-demo-input" />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {EMAIL_EXAMPLES.map((ex, i) => <button key={ex.label} type="button" onClick={() => reset(ex.text)} className="rounded-full border border-mk-border bg-mk-surface px-3 py-1 text-xs text-mk-dim hover:text-mk-text hover:border-mk-lime/50 transition-colors" data-testid={`email-demo-example-${i}`}>{ex.label}</button>)}
        </div>
        <button type="button" onClick={run} disabled={busy || !text.trim()} className="btn-lime mt-4" data-testid="email-demo-run">{busy ? <><Loader2 className="h-4 w-4 animate-spin" />Reading…</> : <><Inbox className="h-4 w-4" />{parsed ? "Read it again" : "Turn it into a job card"}</>}</button>
        <p className="mt-4 text-xs text-mk-muted leading-relaxed">Simulation — it picks out the sender, property, trade, urgency, asset, access window and attachments from whatever you paste. In Fixlr the mailbox is connected, senders are matched to your real clients and properties, and replies thread onto the job.</p>
        {stage >= STAGES.length && (
          <motion.div {...fade} className="mt-5 flex flex-wrap gap-3" data-testid="email-demo-cta">
            <a href={SITE.signupUrl} target="_blank" rel="noreferrer" className="btn-lime !py-2 !px-4 text-sm">Connect your inbox free <ArrowRight className="h-4 w-4" /></a>
            <Link to="/contact" className="btn-ghost !py-2 !px-4 text-sm">Book a demo</Link>
          </motion.div>
        )}
      </div>
      <div className="lg:col-span-7 min-w-0">
        <BrowserFrame url="app.fixlr.app/inbox" className="glow-lime">
          {parsed ? (
            <Panel title="Inbox · 1 email" right={stage >= STAGES.length ? <span data-testid="email-demo-found">{found} of {parsed.fields.length} fields found</span> : "reading…"}>
              <div className="grid lg:grid-cols-[1fr_1.1fr] gap-3">
                <div className="rounded-lg border border-mk-border bg-mk-bg/60 p-2.5 text-[10px] leading-relaxed text-mk-dim whitespace-pre-wrap break-words max-h-80 overflow-y-auto" data-testid="email-demo-highlighted">
                  <div className="flex items-center gap-1.5 text-mk-muted mb-1"><Mail className="h-3 w-3" />Original email</div>
                  {stage >= 1 ? <Highlighted text={parsed.text} spans={parsed.spans} /> : parsed.text}
                </div>
                <JobCard p={parsed} stage={stage} converted={converted} onConvert={() => setConverted(true)} />
              </div>
            </Panel>
          ) : (
            <div className="py-12 text-center" data-testid="email-demo-empty">
              <Inbox className="mx-auto h-8 w-8 text-mk-muted" />
              <div className="mt-3 text-sm text-mk-dim">Paste an email on the left and press <span className="text-mk-text">Turn it into a job card</span>.</div>
              <div className="mt-1 text-xs text-mk-muted">Sender, property, trade, urgency, asset and access window get picked out live.</div>
            </div>
          )}
        </BrowserFrame>
      </div>
    </div>
  );
}
